import { z } from 'zod';

/**
 * Zod schemas for API request validation. Imported by:
 *   - workers/api → @hono/zod-validator runs schemas against incoming requests
 *   - apps/dashboard → typed fetch wrapper validates outbound query params
 *
 * Every schema here MUST line up with the matching DTO in types/api.ts —
 * the type definitions there are the user-facing contract; these schemas
 * are the runtime guards.
 */

// ─── Enum primitives (mirror types/db.ts) ──────────────────────────────────

export const TierSchema = z.enum(['A', 'B', 'C', 'D']);
export const RiskRatingSchema = z.enum(['green', 'yellow', 'red']);
export const RegionSchema = z.enum(['attica', 'corinth']);
export const LeadStatusSchema = z.enum([
  'new',
  'analyzed',
  'scored',
  'reviewed',
  'dismissed',
  'watching',
  'bidding',
]);
export const ManualLeadStatusSchema = z.enum([
  'reviewed',
  'dismissed',
  'watching',
  'bidding',
]);
export const LeadSortFieldSchema = z.enum([
  'opportunity_score',
  'discount_pct',
  'created_at',
  'starting_price',
  'auction_date',
]);
export const SortDirectionSchema = z.enum(['asc', 'desc']);

// ─── Query params (URL strings — coerce to native types) ──────────────────

/**
 * Comma-separated list helper. Hono passes ?tier=A,B as a single string;
 * this turns it into a typed array. Empty string → undefined (no filter).
 */
const csvList = <T extends z.ZodTypeAny>(item: T) =>
  z
    .string()
    .optional()
    .transform((s, ctx) => {
      if (s == null || s === '') return undefined;
      const parts = s
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      const parsed: Array<z.infer<T>> = [];
      for (const p of parts) {
        const r = item.safeParse(p);
        if (!r.success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid value "${p}"`,
          });
          return z.NEVER;
        }
        parsed.push(r.data);
      }
      return parsed.length > 0 ? parsed : undefined;
    });

const positiveNumber = z.coerce.number().nonnegative().finite();
const positiveInt = z.coerce.number().int().nonnegative();
const fraction = z.coerce.number().min(0).max(1);

export const ListLeadsQuerySchema = z.object({
  tier: csvList(TierSchema),
  status: csvList(LeadStatusSchema),
  risk: csvList(RiskRatingSchema),
  municipality: z.string().min(1).optional(),
  region: RegionSchema.optional(),
  round: z.coerce.number().int().min(1).max(10).optional(),
  sqmMin: positiveNumber.optional(),
  sqmMax: positiveNumber.optional(),
  discountMin: fraction.optional(),
  // Query param arrives as the string 'true'/'false'; transform to boolean.
  inIauction: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v == null ? undefined : v === 'true')),
  sort: LeadSortFieldSchema.default('opportunity_score'),
  direction: SortDirectionSchema.default('desc'),
  limit: positiveInt.max(200).default(50),
  offset: positiveInt.default(0),
});

export type ListLeadsQueryParsed = z.infer<typeof ListLeadsQuerySchema>;

// ─── Body validators ───────────────────────────────────────────────────────

export const UpdateLeadStatusBodySchema = z.object({
  status: ManualLeadStatusSchema,
  note: z.string().max(2000).optional(),
});

export const LoginBodySchema = z.object({
  inviteCode: z.string().min(4).max(64),
});

/**
 * Schema for PUT /api/settings — updates the authenticated user's row.
 *
 * `alertTiers` is required (must be a non-empty subset of A/B/C/D).
 * `targetMunicipalities` is optional: null = "all targets", otherwise an
 * explicit allow-list of municipality slugs.
 */
export const UpdateSettingsBodySchema = z.object({
  alertTiers: z.array(TierSchema).min(1).max(4),
  targetMunicipalities: z
    .array(z.string().min(1).max(100).regex(/^[a-z0-9-]+$/))
    .nullable(),
});

// ─── Path params ───────────────────────────────────────────────────────────

export const LeadIdParamSchema = z.object({
  id: z.string().uuid(),
});

export const MunicipalityParamSchema = z.object({
  slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/, 'Invalid slug'),
});
